import { __read, __spread } from "tslib";
/**
 *
 * Stream values can be transformed and controlled by {@link pipe | **piping**} through **operators**.
 * urx includes several operators like [[map]], [[filter]], [[scan]], and [[throttleTime]].
 * The [[withLatestFrom]] operator allows the combination of values from other streams.
 *
 * ```ts
 * const foo = stream<number>()
 *
 * // create an emitter that first adds 2 to the passed value, then multiplies it by * 2
 * const bar = pipe(foo, map(value => value + 2), map(value => value * 2))
 * subscribe(bar, value => console.log(value))
 * publish(foo, 2) // outputs 8
 * ```
 *
 * ### Implementing Custom Operators
 * To implement your own operators, implement the [[Operator]] interface.
 * @packageDocumentation
 */
import { compose, thrush } from './utils';
import { subscribe, reset } from './actions';
import { SUBSCRIBE, RESET } from './constants';
/** @internal */
function combineOperators() {
    var operators = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        operators[_i] = arguments[_i];
    }
    return function (subscriber) {
        return operators.reduceRight(thrush, subscriber);
    };
}
export function pipe(source) {
    var operators = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        operators[_i - 1] = arguments[_i];
    }
    // prettier-ignore
    var project = combineOperators.apply(void 0, __spread(operators));
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (function (action, subscription) {
        switch (action) {
            case SUBSCRIBE:
                return subscribe(source, project(subscription));
            case RESET:
                reset(source);
                return;
        }
    });
}
/**
 * The default [[Comparator]] for [[distinctUntilChanged]] and [[duc]].
 */
export function defaultComparator(previous, next) {
    return previous === next;
}
/**
 * Filters out identical values. Pass an optional [[Comparator]] if you need to filter non-primitive values.
 * ```ts
 * const foo = stream<number>()
 *
 * subscribe(
 *  pipe(foo, distinctUntilChanged()),
 *  console.log
 * ) // will be called only once
 *
 * publish(foo, 42)
 * publish(foo, 42)
 * ```
 */
export function distinctUntilChanged(comparator) {
    if (comparator === void 0) { comparator = defaultComparator; }
    var current;
    return function (done) { return function (next) {
        if (!comparator(current, next)) {
            current = next;
            done(next);
        }
    }; };
}
/**
 * Filters out values for which the predicator does not return `true`-ish.
 * ```ts
 * const foo = stream<number>()
 *
 * subscribe(
 *  pipe(foo, filter(value => value % 2 === 0)),
 *  console.log
 * ) // will be called only with even values
 *
 * publish(foo, 2)
 * publish(foo, 3)
 * publish(foo, 4)
 * publish(foo, 5)
 * ```
 */
export function filter(predicate) {
    return function (done) { return function (value) {
        predicate(value) && done(value);
    }; };
}
/**
 * Maps values using the provided project function.
 * ```ts
 * const foo = stream<number>()
 *
 * subscribe(
 *  pipe(foo, map(value => value * 2)),
 *  console.log
 * ) // 4, 6
 *
 * publish(foo, 2)
 * publish(foo, 3)
 * ```
 */
export function map(project) {
    return function (done) { return compose(done, project); };
}
/**
 * Maps values to the hard-coded value.
 * ```ts
 * const foo = stream<number>()
 *
 * subscribe(
 *  pipe(foo, mapTo(3)),
 *  console.log
 * ) // 3, 3
 *
 * publish(foo, 1)
 * publish(foo, 2)
 * ```
 */
export function mapTo(value) {
    return function (done) { return function () { return done(value); }; };
}
/**
 * Works like Array#reduce.
 * Applies an accumulator function on the emitter, and outputs intermediate result. Starts with the initial value.
 * ```ts
 * const foo = stream<number>()
 *
 * subscribe(
 *  pipe(foo, scan((acc, value) => acc + value, 2),
 *  console.log
 * ) // 3, 5
 *
 * publish(foo, 1)
 * publish(foo, 2)
 * ```
 */
export function scan(scanner, initial) {
    return function (done) { return function (value) { return done((initial = scanner(initial, value))); }; };
}
/**
 * Skips the specified amount of values from the emitter.
 * ```ts
 * const foo = stream<number>()
 *
 * subscribe(
 *  pipe(foo, skip(2)),
 *  console.log
 * ) // 3, 4
 *
 * publish(foo, 1) // skipped
 * publish(foo, 2) // skipped
 * publish(foo, 3)
 * publish(foo, 4)
 * ```
 */
export function skip(times) {
    return function (done) { return function (value) {
        times > 0 ? times-- : done(value);
    }; };
}
/**
 * Throttles flowing values at the provided interval in milliseconds.
 * [Throttle VS Debounce in SO](https://stackoverflow.com/questions/25991367/difference-between-throttling-and-debouncing-a-function).
 *
 * ```ts
 *  const foo = stream<number>()
 *  publish(foo, 1)
 *
 *  setTimeout(() => publish(foo, 2), 20)
 *  setTimeout(() => publish(foo, 3), 20)
 *
 *  subscribe(pipe(foo, throttleTime(50)), val => {
 *    console.log(value); // 3
 *  })
 * ```
 */
export function throttleTime(interval) {
    var currentValue = null;
    var timeout;
    return function (done) { return function (value) {
        currentValue = value;
        if (timeout) {
            return;
        }
        timeout = setTimeout(function () {
            timeout = undefined;
            done(currentValue);
        }, interval);
    }; };
}
/**
 * Debounces flowing values at the provided interval in milliseconds.
 * [Throttle VS Debounce in SO](https://stackoverflow.com/questions/25991367/difference-between-throttling-and-debouncing-a-function).
 *
 * ```ts
 *  const foo = stream<number>()
 *  publish(foo, 1)
 *
 *  setTimeout(() => publish(foo, 2), 20)
 *  setTimeout(() => publish(foo, 3), 20)
 *
 *  subscribe(pipe(foo, debounceTime(50)), val => {
 *    console.log(value); // 3
 *  })
 * ```
 */
export function debounceTime(interval) {
    var currentValue;
    var timeout;
    return function (done) { return function (value) {
        currentValue = value;
        if (timeout) {
            clearTimeout(timeout);
        }
        timeout = setTimeout(function () {
            done(currentValue);
        }, interval);
    }; };
}
export function withLatestFrom() {
    var sources = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        sources[_i] = arguments[_i];
    }
    var values = new Array(sources.length);
    var called = 0;
    var pendingCall = null;
    var allCalled = Math.pow(2, sources.length) - 1;
    sources.forEach(function (source, index) {
        var bit = Math.pow(2, index);
        subscribe(source, function (value) {
            var prevCalled = called;
            called = called | bit;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            values[index] = value;
            if (prevCalled !== allCalled && called === allCalled && pendingCall) {
                pendingCall();
                pendingCall = null;
            }
        });
    });
    return function (done) { return function (value) {
        var call = function () { return done([value].concat(values)); };
        if (called === allCalled) {
            call();
        }
        else {
            pendingCall = call;
        }
    }; };
}
